import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { BusinessKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationChips } from '@frontend/location-chips';
import { Button, Heading, Modal, ModalControlResponse, Text, useTooltip } from '@frontend/design-system';
import {
  AIRBusinessKnowledgeProvider,
  useAIRBusinessKnowledgeStore,
  selectLocationHasErrors,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { DEFAULT_BUSINESS_KNOWLEDGE_DATA } from '../constants';
import { BusinessKnowledgeForm } from './business-knowledge-form';

type Props = {
  modalControls: ModalControlResponse;
  filteredLocationIds: string[];
};

export const BusinessKnowledgeBulkEditModal = ({ modalControls, filteredLocationIds }: Props) => {
  const { draftSettings, updateBulkDraft, markFieldAsDeleted } = useAIRBusinessKnowledgeStore([
    'draftSettings',
    'updateBulkDraft',
    'markFieldAsDeleted',
  ]);

  // Initial state for the bulk edit modal
  const initialState = useMemo(() => {
    if (!modalControls.modalProps.show) {
      return {
        draftSettings: { bulk: cloneDeep(DEFAULT_BUSINESS_KNOWLEDGE_DATA) },
        initialSettings: { bulk: cloneDeep(DEFAULT_BUSINESS_KNOWLEDGE_DATA) },
      };
    }
    if (filteredLocationIds.length === 1) {
      const singleLocState = draftSettings[filteredLocationIds[0]] || DEFAULT_BUSINESS_KNOWLEDGE_DATA;
      return {
        draftSettings: { bulk: cloneDeep(singleLocState) },
        initialSettings: { bulk: cloneDeep(singleLocState) },
      };
    }

    const state: BusinessKnowledge = {};
    filteredLocationIds.forEach((locId) => {
      const locData = draftSettings[locId];
      if (locData) {
        if (locData.addressOverride !== undefined && state.addressOverride === undefined) {
          state.addressOverride = {};
        }
        if (locData.businessWebsiteLink !== undefined && state.businessWebsiteLink === undefined) {
          state.businessWebsiteLink = '';
        }
        if (locData.businessWebsiteFaqLink !== undefined && state.businessWebsiteFaqLink === undefined) {
          state.businessWebsiteFaqLink = '';
        }
      }
    });
    return {
      draftSettings: { bulk: cloneDeep(state) },
      initialSettings: { bulk: cloneDeep(state) },
    };
  }, [modalControls.modalProps.show, filteredLocationIds, draftSettings]);

  const handleConfirm = useCallback(
    (data: BusinessKnowledge, deletedFields: Array<keyof BusinessKnowledge>) => {
      // 1. Update the fields in bulk
      updateBulkDraft(filteredLocationIds, data);

      // 2. Apply explicit field deletions to all targeted locations
      filteredLocationIds.forEach((locationId) => {
        deletedFields.forEach((field) => {
          markFieldAsDeleted(locationId, field);
        });
      });
    },
    [filteredLocationIds, updateBulkDraft, markFieldAsDeleted]
  );

  const presentFields = useMemo<Partial<Record<keyof BusinessKnowledge, boolean>>>(() => {
    const present: Partial<Record<keyof BusinessKnowledge, boolean>> = {};
    if (modalControls.modalProps.show) {
      filteredLocationIds.forEach((locId) => {
        const locData = draftSettings[locId];
        if (locData) {
          (Object.keys(locData) as Array<keyof BusinessKnowledge>).forEach((key) => {
            if (locData[key] !== undefined && locData[key] !== null && locData[key] !== '') {
              present[key] = true;
            }
          });
        }
      });
    }
    return present;
  }, [draftSettings, filteredLocationIds, modalControls.modalProps.show]);

  return (
    <AIRBusinessKnowledgeProvider key={modalControls.modalProps.show ? 'open' : 'closed'} initialState={initialState}>
      <BusinessKnowledgeBulkEditModalContent
        modalControls={modalControls}
        filteredLocationIds={filteredLocationIds}
        onConfirm={handleConfirm}
        presentFields={presentFields}
      />
    </AIRBusinessKnowledgeProvider>
  );
};

const BusinessKnowledgeBulkEditModalContent = ({
  modalControls,
  filteredLocationIds,
  onConfirm,
  presentFields,
}: Props & {
  onConfirm: (data: BusinessKnowledge, deletedFields: Array<keyof BusinessKnowledge>) => void;
  presentFields: Partial<Record<keyof BusinessKnowledge, boolean>>;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { draftSettings, initialSettings, deletedDraftFields, clearDraft, fieldErrors } = useAIRBusinessKnowledgeStore([
    'draftSettings',
    'initialSettings',
    'deletedDraftFields',
    'clearDraft',
    'fieldErrors',
  ]);

  const bulkHasErrors = useAIRBusinessKnowledgeStore(selectLocationHasErrors('bulk'));

  const {
    triggerProps: doneTriggerProps,
    tooltipProps: doneTooltipProps,
    Tooltip: DoneTooltip,
  } = useTooltip({
    placement: 'top',
  });

  const handleDone = () => {
    const localData = draftSettings['bulk'] || ({} as BusinessKnowledge);
    const initialLocalData = initialSettings['bulk'] || ({} as BusinessKnowledge);
    const localDeleted = deletedDraftFields['bulk'] || [];
    const localErrors = fieldErrors['bulk'] || {};

    const validData: BusinessKnowledge = { ...localData };

    // Filter out fields that are NOT dirty or have errors or is empty
    const keys = Object.keys(validData) as Array<keyof BusinessKnowledge>;
    keys.forEach((field) => {
      const hasError = localErrors[field];
      const isDirty = !isEqual(validData[field], initialLocalData[field]);

      if (
        hasError ||
        !isDirty ||
        validData[field] === undefined ||
        validData[field] === null ||
        validData[field] === ''
      ) {
        delete validData[field];
      }
    });

    onConfirm(validData, localDeleted);
    modalControls.closeModal();
  };

  const onClose = () => {
    clearDraft();
    modalControls.closeModal();
  };

  return (
    <Modal {...modalControls.modalProps} minWidth={800}>
      <Modal.Header onClose={onClose}>
        <div
          css={css`
            display: flex;
            gap: var(--fabric-space-gap-sm);
          `}
        >
          <Heading level={2}>{t('Manage All')}</Heading>
          <LocationChips filteredLocationIds={filteredLocationIds} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <BusinessKnowledgeForm locationId='bulk' isManageAll={true} presentFields={presentFields} />
      </Modal.Body>

      <Modal.Footer>
        <div css={styles.actionBar}>
          <Text
            size='medium'
            color='subdued'
            css={css`
              display: flex;
              align-items: center;
              gap: var(--fabric-space-gap-xxs);
            `}
          >
            {t("Click 'Done' to confirm changes only.")}
            <span
              {...doneTriggerProps}
              css={css`
                cursor: pointer;
                display: inline-flex;
              `}
            >
              <Icon color='subdued' name='info-small' />
            </span>
          </Text>
          <Button
            variant='secondary'
            onClick={onClose}
            trackingId={AiReceptionistTrackingIds.businessKnowledgeCancelButton}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDone}
            disabled={bulkHasErrors}
            trackingId={AiReceptionistTrackingIds.businessKnowledgeMultiDoneButton}
          >
            {t('Done')}
          </Button>
        </div>
        <DoneTooltip {...doneTooltipProps} style={{ ...doneTooltipProps.style, maxWidth: '280px' }}>
          {t("AI Receptionist's will only update when you click 'Save' after Manage All is closed.")}
        </DoneTooltip>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  actionBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--fabric-space-gap-sm);
  `,
};
